import { FC } from "react";
import { Link, LoaderFunction, useLoaderData } from "remix";
import { OptionalUser } from "~/lib/auth.server";
import {
  classNames,
  ButtonClasses,
  TryForFreeBlurb,
  QuestionsContactUsBlurb,
} from "~/lib/components";
import { BasicPage } from "~/lib/components/BasicPage";
import { FullUser } from "~/new_shared/db/user.server";
import {
  getUserPerSenderAddressSuffix,
  getUserThxNoThxAddress,
  teamEmailAddress,
  TntAllUrls,
} from "~/new_shared/urls";
import { ProseHeader } from "./faq";

interface LoaderData {
  user: FullUser | undefined;
}

export const loader: LoaderFunction = async ({ request }): Promise<LoaderData> => {
  const { user } = await OptionalUser(request);
  return { user };
};

/* 

TODO: split this into parts:
* "getting it set up"
* "reading your email"
* "cleaning your inbox"

*/

export const HowItWorks: FC<{ user: FullUser | undefined }> = ({ user }) => {
  let num = 0;
  const isUser = !!user;
  const senderAddy = (senderName: string) => senderName + getUserPerSenderAddressSuffix(username);
  const hasUsername = !!user?.username;
  const username = hasUsername ? user.username : "mary";
  const regularAddy = getUserThxNoThxAddress(username);
  const perSenderAddy = (senderName: string) =>
    senderName + getUserPerSenderAddressSuffix(username);
  const genericPerSenderAddress = "{sender name}" + getUserPerSenderAddressSuffix("{username}");
  return (
    <div className="max-w-prose mx-auto text-left">
      <ProseHeader>How do I use ThxNoThx to reduce my inbox clutter?</ProseHeader>
      <ProseHeader>Getting Started</ProseHeader>
      <h2>{++num}. Sign up &amp; Pick your ThxNoThx username</h2>
      <GetUsernameSection
        isUser={isUser}
        username={username}
        regularAddy={regularAddy}
        hasUsername={hasUsername}
      />
      <p>So any email sent to {regularAddy} will go to your ThxNoThx inbox.</p>
      <h2>{++num}. When companies ask for your email, give them your ThxNoThx email address.</h2>
      <p>
        Let's say you are buying something on BobsToyCo.com: when BobsToyCo asks for your email
        address, you enter {regularAddy} instead of your personal email address.
      </p>
      <ProseHeader>What happens when the company sends me email?</ProseHeader>
      <p>
        Now, when you receive emails from BobsToyCo, instead of them cluttering up your personal
        inbox, you will get them bundled into a single daily summary email with all the other emails
        sent to your ThxNoThx address.
      </p>
      <p>
        If later you decide that emails from BobsToyCo are important and you want them to always go
        to your personal inbox, you can stop bundling them by opening up an email from BobsToyCo and
        change the dropdown just below the From line from "bundled" to "forwarded". If you later
        change your mind, you can easily switch it back to bundled.
      </p>
      <p>
        If you need to look at an important email immediately (for example a "verify your email
        address" email) you can check your{" "}
        {isUser ? <Link to={TntAllUrls.Inbox()}>ThxNoThx inbox</Link> : "ThxNoThx inbox"} at any
        time, which has all the recent emails you've received at your ThxNoThx address.
      </p>
      <h2>Some ThxNoThx superpowers</h2>
      <p>
        See all the emails from a sender: Just click on their name in the inbox and you'll see every
        email from that sender
      </p>
      <p>
        Block all emails from a sender: open up an email from BobsToyCo and change the dropdown just
        below the From line from "bundled" to "forwarded". If you later change your mind, you can
        easily switch it back to bundled.
      </p>

      <ProseHeader>
        But what about all those companies that already have my personal email address?
      </ProseHeader>
      <p>ThxNoThx can help with those too!</p>
      <p>
        {" "}
        Just forward an email from your regular inbox to unsubscribe@thxnothx.com and we'll handle
        all the unsubscribing. If a particular company isn't responding to normal unsubscribe
        requests, we'll follow up with them to get you unsubscribed.
        {/* <Link to={TntAllUrls.Faq("rumagic")}>more info</Link> */}
      </p>
    </div>
  );
};

export default function HowItWorksPage() {
  const { user } = useLoaderData();

  return (
    <BasicPage user={user}>
      <HowItWorks user={user} />
      <TryForFreeBlurb user={user} />

      <QuestionsContactUsBlurb />
    </BasicPage>
  );
}

export const GetUsernameSection = ({
  isUser,
  hasUsername,
  username,
  regularAddy,
}: {
  isUser: boolean;
  hasUsername: boolean;
  username: string;
  regularAddy: string;
}) => {
  return (
    <>
      <p>
        {hasUsername ? (
          <>You've already done this! Your username is {username}.</>
        ) : isUser ? (
          <>
            You've got a ThxNoThx account, but you need to pick your username on the{" "}
            <a href={TntAllUrls.AccountSettings("username")}>account settings page</a>. For purposes
            of this example, let's say you pick the ThxNoThx username {username}.
          </>
        ) : (
          <>
            As part of <Link to={TntAllUrls.SignUp()}>creating your account</Link> you will pick a
            username. Let's say you pick {username}.
          </>
        )}{" "}
      </p>
      <p className={classNames(isUser && !hasUsername ? "block" : "hidden")}></p>
    </>
  );
};

/* 

<p>
          For example:
          <ol>
            <li>
              {" "}
              {hasUsername ? (
                <>Your username is {username}.</>
              ) : (
                <>
                  Let's say you choose the username {username} during{" "}
                  <Link to={TntAllUrls.SignUp()}>account creation</Link>.
                </>
              )}{" "}
              That means that any emails sent to any address sent to any email address under{" "}
              {perSenderAddySuffix} will go to your ThxNoThx address.
            </li>
            <li>You shop at BobsToyCo, you give them the address bobstoyco{perSenderAddySuffix}</li>
          </ol>
        </p>
        <p>For example, </p> 

  */

/*
That means any email sent to
{senderAddy("{anything}")}
          will go to your thxnothx inbox.

Let's say you are buying something on BobsToyCo.com, instead of using your personal email
          address ({username}@gmail.com), you enter
{senderAddy("BobsToyCo")} This means that any
          emails sent to {senderAddy("BobsToyCo")} will be marked in your ThxNoThx Inbox as coming from
          BobsToyCo.{" "}
        </p>
        <p>
          Note that you didn't need to do any set up - just receiving email at adress inside of{" "}
          {getUserPerSenderAddressSuffix(username)} for those emails.
        </p>
        <p>
          Later that day she buys a present for her nephew from bobstoyfactory.com - she uses
          bobstoys@mary.thxnothx.com, and emails sent to that address go to the bobstoys folder in
          her ThxNoThx inbox.
        </p>
        <ProseHeader>3. Enjoy your email super powers </ProseHeader>

        <p>
          Later if you get tired of emails from BobsToyCo, you can mute that sender. Now any emails
          that BobsToyCo sends her will not show up in her daily digests and will not show in her
          regular inbox. If she ever needs to see an email that BobsToyCo sent her, she can easily find
          that in her hidden folder.
        </p> 

        */
/*
illustrations: 
https://storyset.com/illustration/credit-card/pana
https://storyset.com/illustration/under-construction/pana

Do I have to add attribution to Storyset whenever I use the illustrations?
Yes, you must always include the attribution to Storyset every time you use our free illustrations.

We recommend placing ‘Illustration by Storyset’ next to the illustrations that is being used and a link to our website. If you can’t add it next to the image, you can place it in the footer of your website, blog or newsletter.

some interesting content stuff; https://www.freepik.com/pricing

*/
